import React from 'react';
import { useIntl } from 'react-intl';
import type { LyricSearchHit, LyricSection } from 'fym-shared/src/music24/lyricSections';
import { TypedMemo } from '../../../../../common/types';
import { LyricLineRow } from './LyricLineRow';
import { lyricPanelMessages } from './lyricPanelMessages';
import { sectionName } from './sectionName';
import type { LyricPick } from './useLyricPick';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric panel; each part uses its own slice of it */
import styles from './LyricLinePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricSearchResultsProps {
  sections: ReadonlyArray<LyricSection>;
  hits: ReadonlyArray<LyricSearchHit>;
  query: string;
  pick: LyricPick;
}

/** The search hits (design b3): a flat list with the section name and the match marked. */
const LyricSearchResultsComponent: React.FC<LyricSearchResultsProps> = ({ sections, hits, query, pick }) => {
  const intl = useIntl();
  const trimmed = query.trim();
  return (
    <div className={styles.group}>
      <div className={styles.groupHead}>
        {intl.formatMessage(lyricPanelMessages.searchHits, { count: hits.length, query: trimmed })}
      </div>
      {hits.map((hit) => {
        const section = sections[hit.sectionIndex];
        const name = sectionName(intl, sections, hit.sectionIndex);
        const repeat =
          section !== undefined && section.repeat > 1
            ? ` ${intl.formatMessage(lyricPanelMessages.repeat, { count: section.repeat })}`
            : '';
        return (
          <LyricLineRow
            key={hit.line}
            line={hit.line}
            state={pick.flatRowState(hit.line)}
            section={`${name}${repeat}`}
            matchStart={hit.matchStart}
            matchLength={trimmed.length}
            onPick={() => pick.pick(hit.sectionIndex, hit.lineIndex)}
          />
        );
      })}
    </div>
  );
};

export const LyricSearchResults = TypedMemo(LyricSearchResultsComponent);
